import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CloudRain, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const SupplyChainWeatherArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-orange-500 text-white">
                <CloudRain className="h-3 w-3 mr-1" />
                {t('article.supplychainweather.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.supplychainweather.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.supplychainweather.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.supplychainweather.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://images.euronews.com/articles/stories/09/57/74/22/1366x768_cmsv2_efef8565-f6b3-5d72-ab9e-c3e6c71811e0-9577422.jpg" 
              alt="European energy infrastructure and grid modernization" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                  {t('supplychainweather.stats.market.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-orange-600">{t('supplychainweather.stats.market.value')}</div>
                <p className="text-sm text-gray-600">{t('supplychainweather.stats.market.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingDown className="h-5 w-5 mr-2 text-blue-600" />
                  {t('supplychainweather.stats.ceasefire.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('supplychainweather.stats.ceasefire.value')}</div>
                <p className="text-sm text-gray-600">{t('supplychainweather.stats.ceasefire.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                  {t('supplychainweather.stats.growth.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('supplychainweather.stats.growth.value')}</div>
                <p className="text-sm text-gray-600">{t('supplychainweather.stats.growth.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('supplychainweather.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('supplychainweather.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('supplychainweather.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('supplychainweather.section2.content')}
            </p>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('supplychainweather.section2.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('supplychainweather.section2.list')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('supplychainweather.section3.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('supplychainweather.section3.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('supplychainweather.section4.title')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('supplychainweather.section4.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('supplychainweather.section4.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('supplychainweather.section5.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('supplychainweather.section5.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('supplychainweather.section6.title')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('supplychainweather.section6.subtitle')}</h4>
              <p className="text-sm text-gray-700">
                {t('supplychainweather.section6.content')}
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('supplychainweather.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://www.euronews.com/my-europe/2025/12/10/european-commission-to-open-up-energy-infrastructure-law-to-speed-up-grid-permits', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default SupplyChainWeatherArticle;